import React, { useState, useEffect } from "react";
import {
  Search, Plus, Download,
  ClipboardList, List, LayoutGrid,
  ArrowUpAZ, ArrowDownZA,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPurchaseList } from "@/redux/Purchase/actions"; // adjust import path
import FileUpload from "../FileUpload/FileUpload";
import { DataTable } from "../ui/table";
import { getPurchaseColumns } from "./columns";
import { useDebounced } from "@/hooks/useDebounced";
import { callLoader } from "@/redux/Loader/actions";
import OrderCard, { PURCHASE_ORDER_MAP } from "../orderCard/OrderCard";

const TITLE = "Purchase Order Management";
function AnimatedTitle() {
  return (
    <motion.h1
      initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight text-gray-900 dark:text-white"
    >
      <motion.span
        className="inline-block overflow-hidden whitespace-nowrap align-bottom"
        initial={{ width: "0ch" }}
        animate={{ width: ["0ch", `${TITLE.length}ch`, `${TITLE.length}ch`, "0ch"] }}
        transition={{ duration: 4.8, times: [0, 0.22, 0.9, 1], ease: ["linear", "linear", "easeInOut"], repeat: Infinity }}
      >
        {TITLE}
      </motion.span>
    </motion.h1>
  );
}

function ViewToggle({ view, setView }) {
  const base = "w-9 h-9 flex items-center justify-center rounded-md transition-colors";
  const active = "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400";
  const inactive = "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100";
  return (
    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-0.5">
      <button onClick={() => setView("table")} title="Table view" className={`${base} ${view === "table" ? active : inactive}`}>
        <List className="w-4 h-4" />
      </button>
      <button onClick={() => setView("card")} title="Card view" className={`${base} ${view === "card" ? active : inactive}`}>
        <LayoutGrid className="w-4 h-4" />
      </button>
    </div>
  );
}

const PurchaseOrderList = ({ onCreateNew, onEditOrder }) => {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [searchText, setSearchText]               = useState("");
  const [searchField, setSearchField]             = useState("ALL");
  const [selectedOrders, setSelectedOrders]       = useState([]);
  const [currentPage, setCurrentPage]             = useState(1);
  const [pageSize, setPageSize]                   = useState(10);
  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId]     = useState(null);
  const [view, setView]                           = useState("table");
  const [sortBy, setSortBy]                       = useState("recent");
  const [sortDirection, setSortDirection]         = useState("desc");

  const { purchaseList, pagination } = useSelector((state) => state.purchase); // adjust slice name
  const dispatch = useDispatch();
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const companyId  = authUser?.companyId;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const debouncedSearch = useDebounced((value) => { _getPurchaseList(1, value); }, 300);
  useEffect(() => { if (searchText === null) return; debouncedSearch(searchText); }, [searchText]);
  useEffect(() => { _getPurchaseList(1); }, [pageSize]);

  const _getPurchaseList = async (page = 1) => {
    const payload = {
      IPageNo: page, IPageRecords: pageSize,
      FilterType: searchField, SearchKeyword: searchText,
      compnayId: companyId || 0,
    };
    dispatch(callLoader(1));
    try {
      await dispatch(getPurchaseList(payload));
      setCurrentPage(page);
    } catch (error) { console.error(error); }
    finally { dispatch(callLoader(0)); }
  };

  const changePage = (page) => _getPurchaseList(page);

  // ── Table column handlers ──────────────────────────────────────────────────
  const handleSelectAll   = (e) => { if (e.target.checked) setSelectedOrders(purchaseList?.map(o => o.rowNum)); else setSelectedOrders([]); };
  const handleSelectOrder = (rowNum) => {
    if (selectedOrders.includes(rowNum)) setSelectedOrders(selectedOrders.filter(id => id !== rowNum));
    else setSelectedOrders([...selectedOrders, rowNum]);
  };

  const columns = getPurchaseColumns({ selectedOrders, purchaseList, handleSelectAll, handleSelectOrder, onEditOrder, setSelectedOrderId });

  const statusOptions = [
    { value: "ALL", label: "All" }, { value: "ACTIVE", label: "Active" }, { value: "CLOSED", label: "Closed" },
  ];

  return (
    <div className="min-h-full space-y-3 p-2 sm:p-3 lg:p-4">

      {/* HEADER CARD */}
      <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-blue-50 dark:bg-blue-900/40 ring-1 ring-blue-100 dark:ring-blue-800 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <AnimatedTitle />
              <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400 hidden sm:block truncate max-w-sm lg:max-w-lg">
                Manage and track all your purchase orders in one place
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 text-[12.5px] font-semibold transition-colors">
              <Download className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">Export</span>
            </button>
            <button onClick={onCreateNew} className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-[12.5px] font-semibold shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)] transition-colors">
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">New Order</span>
              <span className="xs:hidden">New</span>
            </button>
            <ViewToggle view={view} setView={setView} />
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm px-4 sm:px-5 py-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 flex items-center gap-2">
            <select value={searchField} onChange={(e) => setSearchField(e.target.value)}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0">
              <option value="ALL">All Fields</option>
              <option value="PON">PO Number</option>
              <option value="VEND">Vendor</option>
              <option value="DEST">Ship To</option>
              <option value="SKU">SKU</option>
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by PO number, vendor, or destination..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              {statusOptions.map(({ value, label }) => (
                <button key={value} onClick={() => setSearchField(value)}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium transition-all ${searchField === value ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                {sortDirection === "asc" ? <ArrowUpAZ className="w-4 h-4 text-gray-500" /> : <ArrowDownZA className="w-4 h-4 text-gray-500" />}
              </button>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="recent">Most Recent</option>
                <option value="vendor">Vendor Name</option>
                <option value="amount">Order Amount</option>
              </select>
            </div>
          </div>
        </div>
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
            Showing <span className="font-medium text-gray-700 dark:text-gray-300">{pagination?.currentRecord || 0}</span>{" "}
            of <span className="font-medium text-gray-700 dark:text-gray-300">{pagination?.totalRecord || 0}</span> orders
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="p-1">
        {(purchaseList || []).length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
            <ClipboardList className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">No orders found</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try adjusting your search or filter criteria</p>
            <button onClick={onCreateNew} className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
              + New Order
            </button>
          </div>

        ) : view === "card" ? (
         
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(purchaseList || []).map((order) => (
              <OrderCard
                key={order.purchaseOrderId}
                record={order}
                fieldMap={PURCHASE_ORDER_MAP}
                
                navigate={navigate}
                t={(k) => k}
                onEdit={(record) => onEditOrder(record.purchaseOrderId)}
                onOpenFileUpload={() => setSelectedOrderId(order)}
                onOpenNotes={() => {
                    
                  }}
                onDelete={() => {}}
                onRestore={() => {}}
              />
            ))}
          </div>

        ) : (
          /* ── Table view — original DataTable (unchanged) ── */
        <div className="space-y-4">
            <AnimatePresence>
              {(purchaseList || []).map((order) => (
                <OrderCard
                  key={order.purchaseOrderId}
                  record={order}
                  fieldMap={PURCHASE_ORDER_MAP}
                
                  navigate={navigate}
                  t={(k) => k}
                  onOpenFileUpload={() => {
                    setSelectedOrderId(order);
                    setFileUploadVisible(true);
                  }}
                  onOpenNotes={() => {
                   
                  }}
                  onEdit={(record)=> onEditOrder(record.purchaseOrderId)}
                  onDelete={() => {}}
                  onRestore={() => {}}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* PAGINATION */}
        {(purchaseList || []).length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-800 pl-2 pr-16 pt-4 pb-8 mb-4 mt-8 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between rounded-b-xl">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>Show</span>
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>entries</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => currentPage > 1 && changePage(currentPage - 1)} disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors">
                Previous
              </button>
              <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-default">{currentPage}</button>
              <button onClick={() => pagination?.nextPage > 0 && changePage(pagination.nextPage)} disabled={!pagination?.nextPage || pagination.nextPage === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors">
                Next
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing <span className="font-medium">{pagination?.currentRecord || 0}</span>{" "}
              of <span className="font-medium">{pagination?.totalRecord || 0}</span> orders
            </p>
          </div>
        )}
      </div>

      {/* FILE UPLOAD MODAL */}
      {fileUploadVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl">
            <FileUpload orderId={selectedOrderId?.purchaseOrderId} orderNum={selectedOrderId?.poNumber} type="purchase" />
            <div className="flex justify-end mt-6">
              <button onClick={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderList;

// import React, { useEffect, useState } from "react";
// import { Search, Plus, Download, Filter } from "lucide-react";
// import { useDispatch, useSelector } from "react-redux";

// import { getPurchaseList } from "@/redux/Purchase/actions";
// import FileUpload from "../FileUpload/FileUpload";
// import { set } from "date-fns";
// import { DataTable } from "../ui/table";
// import { getPurchaseColumns } from "./columns";
// import { useDebounced } from "@/hooks/useDebounced";
// import { callLoader } from "@/redux/Loader/actions";
// import Pagination from "@/pages/PortPlace/Pagination";

// const PurchaseOrderList = ({ onCreateNew, onEditOrder }) => {
//   const [searchText, setSearchText] = useState(null);
//   const [searchField, setSearchField] = useState("ALL");
//   const [selectedOrders, setSelectedOrders] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(10);

//   const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
//     const userName = authUser?.username;
//     const companyId=authUser?.companyId;

//   const dispatch = useDispatch();
//   const { purchaseList, pagination } = useSelector((state) => state.purchase);

//   const [fileUploadVisible, setFileUploadVisible] = useState(false);
//   const [selectedOrderId, setSelectedOrderId] = useState(null);

//   const debouncedSearch = useDebounced(() => {
//     _getPurchaseList(1);
//   }, 300);
//   useEffect(() => {
//     if (searchText === null) return;
//     debouncedSearch(searchText);
//   }, [searchText]);

//   useEffect(() => {
//     _getPurchaseList(1);
//   }, [pageSize]);

//   useEffect(() => {
//     if (selectedOrderId != null) {
//       setFileUploadVisible(true);
//     } else {
//       setFileUploadVisible(false);
//     }
//   }, [selectedOrderId]);

//   const _getPurchaseList = async (page = 1) => {
//     setCurrentPage(page);
//     const payload = {
//       IPageNo: page,
//       IPageRecords: pageSize,
//       FilterType: searchField,
//       SearchKeyword: searchText || "",
//       compnayId:companyId
//     };
//     try {
//       dispatch(callLoader(1));
//       await dispatch(getPurchaseList(payload));
//       setCurrentPage(page);
//       dispatch(callLoader(0));
//     } catch (error) {
//       dispatch(callLoader(0));
//     }
//   };

//   const changePage = (page) => {
//     _getPurchaseList(page);
//   };
//   const handlePageSize = (size) => {
//     setPageSize(size);
//   };

//   const handleSelectAll = (e) => {
//     if (e.target.checked) {
//       setSelectedOrders(orders.map((order) => order.id));
//     } else {
//       setSelectedOrders([]);
//     }
//   };

//   const handleSelectOrder = (orderId) => {
//     if (selectedOrders.includes(orderId)) {
//       setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
//     } else {
//       setSelectedOrders([...selectedOrders, orderId]);
//     }
//   };

//   const handlers = {
//     selectedOrders,
//     purchaseList,
//     handleSelectAll,
//     handleSelectOrder,
//     onEditOrder,
//     setSelectedOrderId,
//   };

//   const columns = getPurchaseColumns(handlers);
//   return (
//     <div className="min-h-full">
//       {/* Compact Header - Responsive */}
//       <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
//         <div>
//           <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
//             Purchase Orders
//           </h1>
//           <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
//             {pagination?.totalRecord} total orders
//           </p>
//         </div>
//         <div className="flex items-center gap-2 flex-wrap">
//           <button className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all flex items-center justify-center gap-2">
//             <Download className="w-4 h-4" />
//             <span className="text-xs sm:text-sm font-medium">Export</span>
//           </button>
//           <button className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all flex items-center justify-center gap-2">
//             <Filter className="w-4 h-4" />
//             <span className="text-xs sm:text-sm font-medium">Filter</span>
//           </button>
//           <button
//             onClick={onCreateNew}
//             className="flex-1 sm:flex-none px-4 sm:px-5 py-2 bg-[#0072FF] hover:bg-[#0062DD] text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
//           >
//             <Plus className="w-4 h-4" />
//             <span className="text-xs sm:text-sm">New Order</span>
//           </button>
//         </div>
//       </div>

//       {/* Sleek Search Bar - Responsive */}
//       <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-2 sm:p-3 mb-4">
//         <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
//           <select
//             value={searchField}
//             onChange={(e) => setSearchField(e.target.value)}
//             className="px-3 py-2 text-xs sm:text-sm border-0 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
//           >
//             <option value="all">All Fields</option>
//             <option value="order">Order #</option>
//             <option value="vendor">Vendor</option>
//             <option value="status">Status</option>
//           </select>
//           <div className="relative flex-1 max-w-[400px]">
//             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
//             <input
//               type="text"
//               placeholder="Search by order number, vendor, or destination..."
//               value={searchText}
//               onChange={(e) => setSearchText(e.target.value)}
//               className="w-full pl-10 pr-4 py-2 text-xs sm:text-sm border-0 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-[#0072FF] focus:bg-white dark:focus:bg-slate-900 transition-all"
//             />
//           </div>
//         </div>
//       </div>

//       <DataTable
//         data={purchaseList || []}
//         columns={columns}
//         boxClass="shadow-sm border border-slate-200 dark:border-slate-700"
//         trClass="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
//         thClass="px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap uppercase tracking-wider"
//         tdClass="text-sm px-2 py-2 border-b border-slate-200 dark:border-slate-700"
//         htrClass="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50"
//       />

//       <Pagination
//         className="border-t border-slate-200 dark:border-slate-800 pl-2 pr-16 pt-4 pb-8 mb-0 bg-slate-50 dark:bg-slate-800/50 mt-8"
//         currentPage={currentPage}
//         recordsPerPage={pageSize}
//         setRecordsPerPage={handlePageSize}
//         onPrevious={changePage}
//         onNext={changePage}
//         totalRecord={pagination?.totalRecord}
//         handleGoto={changePage}
//         title="purchase Orders"
//       />

//       {fileUploadVisible && (
//         <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
//           <div className="bg-white w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
//             <div>
//               <FileUpload
//                 orderId={selectedOrderId?.purchaseOrderId}
//                 orderNum={selectedOrderId?.poNumber}
//                 type={"purchase"}
//               />
//             </div>
//             <div className="flex justify-end mt-6">
//               <button
//                 onClick={() => {
//                   setFileUploadVisible(false);
//                   setSelectedOrderId(null);
//                 }}
//                 className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default PurchaseOrderList;
